import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LocationProvider } from "@/context/LocationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import ContactDetail from "@/pages/ContactDetail";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import Templates from "@/pages/Templates";
import TemplateBuilder from "@/pages/TemplateBuilder";
import Inbox from "@/pages/Inbox";
import Automations from "@/pages/Automations";
import Social from "@/pages/Social";
import Blog from "@/pages/Blog";
import Settings from "@/pages/Settings";
import Signin from "@/pages/Signin";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import InviteAccept from "@/pages/InviteAccept";
import Onboarding from "@/pages/Onboarding";
import Pricing from "@/pages/Pricing";
import Unsubscribe from "@/pages/Unsubscribe";
import Pipeline from "@/pages/Pipeline";
import Sequences from "@/pages/Sequences";
import Launchpad from "@/pages/Launchpad";
import Calendars from "@/pages/Calendars";
import Payments from "@/pages/Payments";
import Reputation from "@/pages/Reputation";
import Memberships from "@/pages/Memberships";
import Sites from "@/pages/Sites";
import MediaStorage from "@/pages/MediaStorage";
import Reporting from "@/pages/Reporting";
import AppMarketplace from "@/pages/AppMarketplace";
import AIStudio from "@/pages/AIStudio";
import AIAgents from "@/pages/AIAgents";
import Referrals from "@/pages/Referrals";
import Marketing from "@/pages/Marketing";
import ClientPortal from "@/pages/ClientPortal";
import CertificateVerification from "@/pages/CertificateVerification";
import PublicCheckout from "@/pages/PublicCheckout";
import FunnelView from "@/pages/FunnelView";
import ReviewSubmission from "@/pages/ReviewSubmission";
import PublicBooking from "@/pages/PublicBooking";
import NotFound from "@/pages/NotFound";

function RefRedirect() {
  const { code } = useParams();
  return <Navigate to={`/signup?ref=${encodeURIComponent(code || "")}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <LocationProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/signin" element={<Signin />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/invite/:token" element={<InviteAccept />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/portal" element={<ClientPortal />} />
              <Route path="/portal/:id" element={<ClientPortal />} />
              <Route path="/verify/:id" element={<CertificateVerification />} />
              <Route path="/verify" element={<CertificateVerification />} />
              <Route path="/pay/:id" element={<PublicCheckout />} />
              <Route path="/funnel/:slug" element={<FunnelView />} />
              <Route path="/funnels/:slug" element={<FunnelView />} />
              <Route path="/review/:slug" element={<ReviewSubmission />} />
              <Route path="/review" element={<ReviewSubmission />} />
              <Route path="/book/:slug" element={<PublicBooking />} />
              <Route path="/book" element={<PublicBooking />} />
              <Route path="/ref/:code" element={<RefRedirect />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/launchpad" element={<Launchpad />} />
                <Route path="/calendars" element={<Calendars />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/reputation" element={<Reputation />} />
                <Route path="/memberships" element={<Memberships />} />
                <Route path="/sites" element={<Sites />} />
                <Route path="/media" element={<MediaStorage />} />
                <Route path="/reporting" element={<Reporting />} />
                <Route path="/marketplace" element={<AppMarketplace />} />
                <Route path="/marketing" element={<Marketing />} />
                <Route path="/ai-studio" element={<AIStudio />} />
                <Route path="/ai-agents" element={<AIAgents />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/campaigns/:id" element={<CampaignDetail />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/templates/builder/:id" element={<TemplateBuilder />} />
                <Route path="/templates/builder" element={<TemplateBuilder />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/automations" element={<Automations />} />
                <Route path="/sequences" element={<Sequences />} />
                <Route path="/social" element={<Social />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing/cancel" element={<BillingCancel />} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
                <Route path="/conversations" element={<Inbox />} />
              </Route>

              {/* Public aliases */}
              <Route path="/login" element={<Navigate to="/signin" replace />} />
              <Route path="/admin-login" element={<Navigate to="/signin" replace />} />
              <Route path="/admin/login" element={<Navigate to="/signin" replace />} />

              {/* Universal 404 Broken Page for any invalid/broken URL on the domain */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </LocationProvider>
      </ThemeProvider>
    </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
